import { 
  Box, Button, Grid, Group, InputLabel, LoadingOverlay, 
  Select, Paper, SimpleGrid, Stack, TextInput, Title, 
  Text, Collapse, Divider, Breadcrumbs, Anchor, PasswordInput
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Form, ResourceAvatar, useMedplum } from '@medplum/react';
import { formatFamilyName, formatGivenName, formatHumanName, normalizeErrorString } from '@medplum/core';
import { Address, HumanName, Patient, Attachment } from '@medplum/fhirtypes';
import { IconCircleCheck, IconCircleOff, IconUpload, IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState, useCallback, useRef, type JSX } from 'react';
import { InfoSection } from '../../components/InfoSection';
import { useDisclosure } from '@mantine/hooks';
import { AddressForm } from '../../components/CustomAddress';
import { Link } from 'react-router-dom';

export function Profile(): JSX.Element | null {
  const medplum = useMedplum();
  const profileData = medplum.getProfile();
  
  // Ensure profileData is not null and is a Patient resource
  const [profile, setProfile] = useState<Patient | null>(
    profileData?.resourceType === 'Patient' ? profileData as Patient : null
  );

  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Initialize address safely
  const [address, setAddress] = useState<Address>(
    profile?.address?.[0] || {}
  );

  const [avatarHover, { open: showAvatarOverlay, close: hideAvatarOverlay }] = useDisclosure(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // State for password change
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  
  // State and Ref for avatar upload
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Initial values - handle potential null profile
  const initialGivenName = profile ? formatGivenName(profile.name?.[0] as HumanName) || '' : '';
  const initialFamilyName = profile ? formatFamilyName(profile.name?.[0] as HumanName) || '' : '';
  const initialEmail = profile?.telecom?.find((t) => t.system === 'email')?.value || '';

  // Form field refs
  const givenNameRef = useRef<HTMLInputElement>(null);
  const familyNameRef = useRef<HTMLInputElement>(null);
  
  // Check if profile exists before proceeding
  useEffect(() => {
    const currentProfile = medplum.getProfile();
    if (currentProfile?.resourceType === 'Patient') {
      setProfile(currentProfile as Patient);
      setAddress(currentProfile.address?.[0] || {});
    } else {
      // Handle case where profile is not loaded or not a Patient
      console.error("Profile not loaded or is not a Patient resource.");
      // Potentially redirect or show an error message
    }
  }, [medplum]);

  // Track input changes to detect unsaved changes
  const checkForChanges = useCallback(() => {
    if (!profile) return; // Don't check changes if profile isn't loaded

    const currentGivenName = givenNameRef.current?.value || '';
    const currentFamilyName = familyNameRef.current?.value || '';
    
    const addressChanged = JSON.stringify(address) !== JSON.stringify(profile.address?.[0] || {});
    const fieldsChanged = 
      currentGivenName !== initialGivenName ||
      currentFamilyName !== initialFamilyName;
    
    setHasChanges(fieldsChanged || addressChanged);
  }, [address, profile, initialGivenName, initialFamilyName]);
  
  // Handle address changes
  const handleAddressChange = useCallback((newAddress: Address) => {
    setAddress(newAddress);
    checkForChanges(); // Check for changes immediately after address update
  }, [checkForChanges]);
  
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!givenNameRef.current?.value) {
      newErrors.givenName = 'First name is required';
    }
    
    if (!familyNameRef.current?.value) {
      newErrors.familyName = 'Last name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Warn about unsaved changes when leaving page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = ''; // Standard for most browsers
        return ''; // For older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  async function handleProfileEdit(formData: Record<string, string>): Promise<void> {
    if (!profile || !validateForm()) {
      return;
    }
    
    setLoading(true);
    const newProfile: Patient = {
      ...profile,
      name: [
        {
          use: 'official',
          given: [formData.givenName],
          family: formData.familyName,
        },
      ],
      address: [address], // Use state value
    };
    
    try {
      const updatedProfile = await medplum.updateResource(newProfile);
      showNotification({
        icon: <IconCircleCheck size="1.2rem" />,
        title: 'Success',
        message: 'Your profile has been updated',
        color: 'teal',
      });
      
      setProfile(updatedProfile as Patient); // Update local profile state
      setAddress(updatedProfile.address?.[0] || {}); // Update local address state
      // Recalculate initial values based on the *newly saved* profile state if needed, or just reset hasChanges
      setHasChanges(false); 
      // It might be safer to refetch initial values here if needed, but simply resetting hasChanges is common.
      window.scrollTo(0, 0); 
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size="1.2rem" />,
        title: 'Error',
        message: normalizeErrorString(err),
      });
    } finally {
      setLoading(false);
    }
  }

  // Handler for password change - RESTORE
  async function handlePasswordChange(event: React.FormEvent): Promise<void> {
    event.preventDefault(); // Prevent default form submission if wrapped in a form
    setPasswordChangeError(''); // Clear previous errors
    
    if (newPassword !== confirmPassword) {
      setPasswordChangeError('New passwords do not match.');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordChangeError('New password must be at least 8 characters long.');
      return;
    }
    
    setPasswordChangeLoading(true);
    try {
      await medplum.post('auth/changepassword', {
        oldPassword,
        newPassword,
      });
      
      showNotification({
        icon: <IconCircleCheck size="1.2rem" />,
        title: 'Success',
        message: 'Password changed successfully',
        color: 'teal',
      });
      
      // Clear fields on success
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (err) {
      console.error('Password change error:', err);
      setPasswordChangeError(normalizeErrorString(err));
      showNotification({
        color: 'red',
        icon: <IconCircleOff size="1.2rem" />,
        title: 'Password Change Failed',
        message: normalizeErrorString(err),
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  }

  // Handler for avatar click
  const handleAvatarClick = () => {
    // Implementation of handleAvatarClick
  };

  // Handler for file selection and upload
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Implementation of handleFileChange
  };

  // Render loading or null if profile isn't available yet
  if (!profile) {
    return <LoadingOverlay visible={true} />; // Or some other placeholder/loading state
  }

  // Breadcrumbs items
  const breadcrumbItems = [
    { title: 'Home', href: '/' },
    { title: 'Profile', href: '/account/profile' },
  ].map((item, index) => (
    <Anchor component={Link} to={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  return (
    // Outer container with background
    <div className="min-h-screen bg-gray-50">
      {/* Inner container for content centering and padding */}
      <div className="container mx-auto px-4 pt-8 pb-16">
        <LoadingOverlay visible={loading} />
        
        {/* Breadcrumbs */}
        <Box mb="lg">
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Box>

        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
            Your <span className="text-[#C2A4D6]">Profile</span> Settings
          </h1>
          <p className="text-lg text-gray-600 font-['Merriweather'] mb-4">
            Manage your personal and contact information
          </p>
          {hasChanges && (
             <Text color="orange" size="sm" mt="xs">
               You have unsaved changes. Remember to save!
             </Text>
          )}
        </div>

        {/* Main content area */}
        <div className="max-w-4xl mx-auto">
          <Form onSubmit={handleProfileEdit}>
            <Stack gap="lg">
              {/* Avatar Section */}
              <Box className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6 p-6 text-center">
                <Title order={4} mb="md" className="text-gray-800">Profile Photo</Title>
                <Box 
                  pos="relative" 
                  display="inline-block"
                  onMouseEnter={showAvatarOverlay}
                  onMouseLeave={hideAvatarOverlay}
                  style={{ cursor: 'pointer' }}
                  onClick={handleAvatarClick}
                  className="mb-3"
                >
                  <Box pos="relative">
                    {/* Conditional rendering: Image or Initials Avatar */}
                    {profile.photo?.[0]?.data && profile.photo[0].contentType ? (
                      <img 
                        src={`data:${profile.photo[0].contentType};base64,${profile.photo[0].data}`}
                        alt="Profile Avatar"
                        style={{
                          width: 120, 
                          height: 120, 
                          borderRadius: '50%', // Make it circular
                          objectFit: 'cover' // Ensure image covers the area well
                        }}
                      />
                    ) : (
                      <ResourceAvatar size={120} radius={60} value={profile} /> 
                    )}
                    <Collapse in={avatarHover}>
                      <Box 
                        pos="absolute" 
                        top={0} 
                        left={0} 
                        right={0} 
                        bottom={0} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '60px',
                        }}
                      >
                        <IconUpload color="white" size={32} />
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              </Box>

              {/* Personal Information Section - RENAME AND MERGE */}
              <Box className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6 p-6">
                <Title order={4} mb="md" className="text-gray-800">Profile Details</Title>
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="First Name"
                      name="givenName"
                      required
                      placeholder="Enter your first name"
                      defaultValue={initialGivenName}
                      ref={givenNameRef}
                      onChange={checkForChanges}
                      error={errors.givenName}
                      size="md"
                      mb="md"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Last Name"
                      name="familyName"
                      required
                      placeholder="Enter your last name"
                      defaultValue={initialFamilyName}
                      ref={familyNameRef}
                      onChange={checkForChanges}
                      error={errors.familyName}
                      size="md"
                      mb="md"
                    />
                  </Grid.Col>
                </Grid>

                {/* Moved Contact Fields */}
                <Grid>
                  <Grid.Col span={12}> 
                    <TextInput
                      label="Email"
                      name="email"
                      disabled
                      defaultValue={initialEmail}
                      size="md"
                      mb="md"
                    />
                  </Grid.Col>
                </Grid>

                {/* Divider before Save Button */}
                <Divider my="lg" />
                
                {/* Moved Save Button */}
                <Group justify="space-between" align="center" mt="xl">
                  {hasChanges && (
                    <Text color="orange" size="sm">
                      You have unsaved changes
                    </Text>
                  )}
                  <Button 
                    type="submit" 
                    disabled={!hasChanges || loading}
                    leftSection={<IconDeviceFloppy size="1rem" />}
                    size="md"
                    radius="xl"
                    className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white disabled:bg-gray-300 ml-auto" 
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Group>
              </Box>
            </Stack>
          </Form>
          
          {/* Security Settings Section - RESTORED */}
          <Box className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6 p-6 mt-lg"> {/* Added mt-lg for spacing */}
            <Title order={4} mb="md" className="text-gray-800">Security Settings</Title>
            
            <form onSubmit={handlePasswordChange}> {/* Ensure handlePasswordChange function exists */}
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <PasswordInput
                    label="Current Password"
                    placeholder="Enter your current password"
                    value={oldPassword} // Ensure state exists
                    onChange={(event) => setOldPassword(event.currentTarget.value)} // Ensure state setter exists
                    required
                    size="md"
                    mb="md"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  {/* Empty column for balance */}
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <PasswordInput
                    label="New Password"
                    placeholder="Enter new password (min. 8 characters)"
                    value={newPassword} // Ensure state exists
                    onChange={(event) => setNewPassword(event.currentTarget.value)} // Ensure state setter exists
                    required
                    size="md"
                    mb="md"
                  />
                  <PasswordInput
                    label="Confirm New Password"
                    placeholder="Confirm your new password"
                    value={confirmPassword} // Ensure state exists
                    onChange={(event) => setConfirmPassword(event.currentTarget.value)} // Ensure state setter exists
                    required
                    size="md"
                    error={passwordChangeError && passwordChangeError.includes('match') ? passwordChangeError : undefined} // Ensure state exists
                    mb="md"
                  />
                </Grid.Col>
              </Grid>
              
              {/* Display general password change errors */}
              {passwordChangeError && !passwordChangeError.includes('match') && ( // Ensure state exists
                (<Text color="red" size="sm" mb="md">
                  {passwordChangeError}
                </Text>)
              )}

              <Group justify="flex-end" mt="md">
                <Button 
                  type="submit" 
                  loading={passwordChangeLoading} // Ensure state exists
                  disabled={!oldPassword || !newPassword || !confirmPassword || passwordChangeLoading} // Ensure state exists
                  size="md"
                  radius="xl"
                  className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white disabled:bg-gray-300"
                >
                  Change Password
                </Button>
              </Group>
            </form>
          </Box>
          
        </div>
      </div>
    </div>
  );
}

export default Profile; // Optional: Add default export if needed
